import { memo, type ReactNode } from 'react';
import { type Components } from 'react-markdown';

import { cn } from '@air/utils-tailwind';

export type InlinePreviewSpanProps = {
  children: ReactNode;
  className?: string;
};

const InlinePreviewSpan = memo(({ children, className }: InlinePreviewSpanProps) => (
  <span className={cn('inline', className)}>{children}</span>
));

InlinePreviewSpan.displayName = 'InlinePreviewSpan';

/**
 * Inline-only react-markdown map for the My Tasks row preview. Block tags flatten to spans so the
 * outer row can apply single-line truncation.
 */
export const WORKSPACE_AGENT_SESSION_ROW_PREVIEW_MARKDOWN_COMPONENTS: Components = {
  a: ({ children }) => <InlinePreviewSpan className="underline">{children}</InlinePreviewSpan>,
  blockquote: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  code: ({ children, className }) => {
    const isInline = !className;

    return isInline ? (
      <code className="break-all px-0.5 text-10">{children}</code>
    ) : (
      <code className={cn('text-10', className)}>{children}</code>
    );
  },
  del: ({ children }) => <InlinePreviewSpan className="line-through">{children}</InlinePreviewSpan>,
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  h2: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  h3: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  h4: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  h5: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  h6: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  hr: () => null,
  img: ({ alt }) => <InlinePreviewSpan>{alt ?? ''}</InlinePreviewSpan>,
  input: () => null,
  li: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  ol: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  p: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  pre: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  strong: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  table: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  tbody: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  td: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  th: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  thead: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  tr: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
  ul: ({ children }) => <InlinePreviewSpan>{children}</InlinePreviewSpan>,
};
